<template>
  <VBusHelper
    :reactOn="constants.EVENTS.WALLET.DEPOSIT_OPEN"
    :handler="showModal"
  />

  <DialogComponent
    ref="modal"
    dialogId="deposit-modal"
    type="bottomsheet"
    noInnerPadding
    @closed="resetForm"
  >
    <div class="">
      <!-- Header with gradient -->
      <div class="relative bg-primary rounded-t-3xl px-6 pt-8 pb-10">
        <div class="text-center">
          <div
            class="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-4 shadow-lg"
          >
            <svg
              class="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M12 4v16m8-8H4"
              />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-white">Dépôt d'argent</h2>
          <p class="text-emerald-100 text-sm mt-1">
            Ajoutez des fonds à votre portefeuille
          </p>
        </div>
      </div>

      <!-- Content Card -->
      <div class="relative -mt-6 px-4 pb-4">
        <div
          class="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-6 space-y-6"
        >
          <!-- Amount Section -->
          <div class="space-y-3">
            <label
              class="block text-sm font-semibold text-slate-700 flex items-center gap-2"
            >
              <svg
                class="w-4 h-4 text-emerald-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Montant à déposer
            </label>

            <!-- Amount Input Display -->
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
              >
                <span class="text-slate-400 font-semibold text-lg">XOF</span>
              </div>
              <input
                v-model="form.amount"
                type="number"
                name="amount"
                min="100"
                max="500000"
                class=" placeholder:text-gray-300 w-full pl-16 pr-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-2xl text-3xl font-bold text-slate-800 text-center focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 transition-all outline-none"
                :class="{
                  'border-red-300 bg-red-50': errors.amount,
                  'border-emerald-500 bg-emerald-50':
                    form.amount && !errors.amount
                }"
                placeholder="5000"
                @input="validateAmount"
              />
            </div>

            <div class="flex justify-between text-xs text-slate-500 px-2">
              <span>Min: 100 XOF</span>
              <span>Max: 500 000 XOF</span>
            </div>

            <div
              v-if="errors.amount"
              class="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-xl"
            >
              <svg
                class="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ errors.amount }}
            </div>
          </div>

          <!-- Quick Amounts -->
          <div class="space-y-3">
            <label class="block text-sm font-semibold text-slate-700"
              >Montants rapides</label
            >
            <div class="grid grid-cols-3 gap-2">
              <button
                v-for="amount in quickAmounts"
                :key="amount"
                type="button"
                class="py-3 px-2 rounded-xl font-semibold text-sm transition-all duration-300 border-2"
                :class="
                  form.amount == amount
                    ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/25 scale-105'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:scale-105'
                "
                @click="selectQuickAmount(amount)"
              >
                {{ formatAmount(amount) }}
              </button>
            </div>
          </div>

          <!-- Phone Number -->
          <div class="space-y-3">
            <label
              class="block text-sm font-semibold text-slate-700 flex items-center gap-2"
            >
              <svg
                class="w-4 h-4 text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Numéro de téléphone
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
              >
                <svg
                  class="w-5 h-5 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <input
                v-model="form.phoneNumber"
                type="tel"
                name="phoneNumber"
                class="w-full pl-12 pr-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 font-medium focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 transition-all outline-none"
                :class="{
                  'border-red-300 bg-red-50': errors.phoneNumber,
                  'border-emerald-500 bg-emerald-50':
                    form.phoneNumber && !errors.phoneNumber
                }"
                placeholder="01 XX XX XX XX"
                @input="validatePhoneNumber"
              />
            </div>
            <div
              v-if="errors.phoneNumber"
              class="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-xl"
            >
              <svg
                class="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ errors.phoneNumber }}
            </div>
          </div>

          <!-- Processing Indicator -->
          <div
            v-if="isProcessing"
            class="bg-primary-50 border-2 border-primary-200 rounded-2xl p-4"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-primary-600 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <div class="flex-1">
                <p class="text-sm font-semibold text-primary-800">
                  Traitement en cours...
                </p>
                <p class="text-xs text-primary-600 mt-0.5">
                  Veuillez valider le paiement sur votre téléphone
                </p>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <button
            type="button"
            class="w-full py-4 rounded-2xl font-bold text-lg transition-all duration-300 active:scale-95 flex items-center justify-center gap-3"
            :class="
              isFormValid && !isProcessing
                ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-[1.02]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            "
            :disabled="!isFormValid || isProcessing"
            @click="handleSubmit"
          >
            <template v-if="!isProcessing">
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Confirmer le dépôt
            </template>
            <template v-else>
              <svg class="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Traitement en cours...
            </template>
          </button>

          <!-- Cancel Button -->
          <button
            v-if="!isProcessing"
            type="button"
            class="w-full py-3.5 rounded-xl font-semibold text-slate-600 hover:bg-slate-100 transition-all duration-300 active:scale-95"
            @click="hideModal"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';

const modal = ref(null);
const form = ref({
  amount: '',
  phoneNumber: ''
});
let depositTimeout = null;
const errors = ref({
  amount: null,
  phoneNumber: null
});
const isProcessing = ref(false);

const quickAmounts = [500, 1000, 2000, 5000, 10000, 20000];

function showModal() {
  modal.value?.show();
}

function hideModal() {
  modal.value?.hide();
}

function formatAmount(amount) {
  return new Intl.NumberFormat('fr-FR').format(amount);
}

function selectQuickAmount(amount) {
  form.value.amount = amount;
  validateAmount();
}

function validateAmount() {
  const amount = Number(form.value.amount);
  if (!amount) {
    errors.value.amount = 'Veuillez entrer un montant';
    return;
  }
  if (amount < 100) {
    errors.value.amount = 'Le montant minimum est de 100 XOF';
    return;
  }
  if (amount > 500000) {
    errors.value.amount = 'Le montant maximum est de 500 000 XOF';
    return;
  }
  errors.value.amount = null;
}

function validatePhoneNumber() {
  const phoneRegex = /^[0-9]{8,12}$/;
  const phone = form.value.phoneNumber.replace(/\s/g, '');
  if (!phone) {
    errors.value.phoneNumber = 'Veuillez entrer votre numéro';
    return;
  }
  if (!phoneRegex.test(phone)) {
    errors.value.phoneNumber = 'Numéro de téléphone invalide';
    return;
  }
  errors.value.phoneNumber = null;
}

const isFormValid = computed(() => {
  return (
    form.value.amount &&
    Number(form.value.amount) >= 100 &&
    Number(form.value.amount) <= 500000 &&
    form.value.phoneNumber &&
    form.value.phoneNumber.length >= 8 &&
    !errors.value.amount &&
    !errors.value.phoneNumber
  );
});

function handleSubmit() {
  if (!isFormValid.value) return;

  isProcessing.value = true;

  bus.emit(constants.EVENTS.WALLET.DEPOSIT_INITIATE, {
    amount: Number(form.value.amount),
    phoneNumber: form.value.phoneNumber.replace(/\s/g, ''),
    currency: 'XOF',
    timestamp: Date.now()
  });

  if (depositTimeout) clearTimeout(depositTimeout);
  depositTimeout = setTimeout(() => {
    if (isProcessing.value) {
      isProcessing.value = false;
      hideModal();
      resetForm();
    }
  }, 10000);
}

function resetForm() {
  form.value = { amount: '', phoneNumber: '' };
  errors.value = { amount: null, phoneNumber: null };
  isProcessing.value = false;
}

function onDepositSuccess() {
  if (depositTimeout) clearTimeout(depositTimeout);
  isProcessing.value = false;
  hideModal();
  resetForm();
}

function onDepositError() {
  if (depositTimeout) clearTimeout(depositTimeout);
  isProcessing.value = false;
}

onMounted(() => {
  bus.on(constants.EVENTS.WALLET.DEPOSIT_SUCCESS, onDepositSuccess);
  bus.on(constants.EVENTS.WALLET.DEPOSIT_ERROR, onDepositError);
});

onBeforeUnmount(() => {
  bus.off(constants.EVENTS.WALLET.DEPOSIT_SUCCESS, onDepositSuccess);
  bus.off(constants.EVENTS.WALLET.DEPOSIT_ERROR, onDepositError);
});
</script>